<template>
  <div v-if="isLockedBidding && !isScreenL" class="place-bid-wrapper">
    <div class="locked-bidding-btn-wrapper">
      <div class="label-locked-btn">
        {{ trans("live.bidding.bidding_locked") }}
      </div>
      <a class="link link-dashed" @click="openLockBiddingTab">{{ trans("live.bidding.unlock_bidding_tab") }}</a>
    </div>
  </div>

  <div v-else-if="isAuctionStarted" class="place-bid-wrapper">
    <!-- If currency converter enabled, add class "currency-converter-enabled" in place-bid-block  -->
    <div class="place-bid-block">
      <div class="current-bid-anim">
        <transition :name="isPageVisible ? 'current-bid' : ''">
          <div class="current-bid" :key="currentBid ?? 0">
            {{ format_currency(currentBid) }}
          </div>
        </transition>
      </div>

      <currency-converted />

      <div class="bid-controllers" v-if="auctionStore.user">
        <div
          :class="['bid-controller btn-minus', { disabled: !bidVariants || selectedBid === bidVariants[0] }]"
          @click="selectedBidMinus">
          <div class="icon-minus"></div>
        </div>
        <div class="switch-block-bid-button-mob">
          <label class="switch">
            <input v-model="isLockedBidding" type="checkbox" />
            <span class="slider round"></span>
            <base-icon icon-name="lock" :class="{ show: !isLockedBidding }" view-box="0 0 17.721 23.259">
              <lock-icon></lock-icon>
            </base-icon>
            <base-icon icon-name="unlock" :class="{ show: isLockedBidding }" view-box="0 0 18 24">
              <unlock-icon></unlock-icon>
            </base-icon>
          </label>
          <span class="locked-button-label">{{ isLockedBidding ? trans("live.bidding.unlock") : trans("live.bidding.lock") }}</span>
        </div>
        <template v-if="!isLockedBidding">
          <div class="place-bid-btn btn-sky-blue btn-click-effect" @click="placeBid(selectedBid)">
            {{ trans("live.bidding.place_bid_btn", { amount: format_currency(selectedBid) }) }}
          </div>
          <div class="bid-controller btn-plus" @click="selectedBidPlus">
            <div class="icon-plus"></div>
          </div>
        </template>
        <div v-else class="label-locked-btn">
          {{ trans("live.bidding.bidding_locked") }}
        </div>
      </div>
      <div v-else class="need-login-block">
        <a href="/login" class="btn btn-sky-blue need-login-btn">
          {{ trans("live.bidding.log_in") }}
        </a>
      </div>
    </div>
    <div class="fast-bids-short" v-if="auctionStore.user && !isLockedBidding">
      <div class="fast-bids-variants">
        <div
          class="fast-bid-variant"
          v-for="(variant, index) in fastBidVariants"
          :key="index"
          @click="placeBid(variant)">
          {{ format_currency(variant) }}
        </div>
      </div>

      <fast-bid-variants-toggle
        wrapper-class=".live-bid-block .place-bid-wrapper"
        :target="
          !props.isMobileBlock ? '.main-panel .fast-bids-variants' : '.mobile-place-bid-block .fast-bids-variants'
        ">
        <div class="fast-bids-toggle-show"></div>
      </fast-bid-variants-toggle>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, watch, onMounted, onUnmounted, ref, defineProps, Ref } from "vue";
  import Api from "@/services/api";
  import FastBidVariantsToggle from "@/components/_partials/FastBidVariantsToggle.vue";
  import { IPopupError } from "@/models";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { useToast } from "primevue/usetoast";
  import { format_currency } from "@/utils/currencies";
  import { trans } from "@/utils/trans";
  import CurrencyConverted from "@/components/_partials/CurrencyConverted.vue";
  import { LockIcon, UnlockIcon, BaseIcon } from "@/components/icons";

  const toast = useToast();
  const emit = defineEmits(["changeTab"]);

  const auctionStore = useAuctionStore();
  const lots = computed(() => auctionStore.lots);
  const liveLot = computed(() => lots.value.find((item) => item.status === LotStatuses.LIVE));
  const endedLots = computed(() => lots.value.filter((item) => item.status === LotStatuses.ENDED));
  const isAuctionStarted = computed(() => liveLot.value?.id || endedLots.value.length);

  const activeLot = computed(() => auctionStore.activeLot);
  const currentBid = computed(() => auctionStore.activeLot?.current_bid);
  const bidsCount = computed(() => auctionStore.activeLot?.bids_count);
  const userBid = computed(() => (activeLot.value?.id ? auctionStore.user?.bids[activeLot.value?.id] : 0));

  const props = withDefaults(
    defineProps<{
      isMobileBlock?: boolean;
    }>(),
    {
      isMobileBlock: false
    }
  );

  const isLockedBidding = computed({
    get: () => auctionStore.isLockedBidding,
    set: (value) => {
      auctionStore.$patch({ isLockedBidding: value });
    }
  });

  const bidVariants = ref(auctionStore.auction.bid_variants);
  const selectedBid = ref(0);
  const isPageVisible = ref(!document.hidden);

  const fastBidVariants = computed(() => bidVariants.value.filter((item) => item > selectedBid.value).slice(0, 3));

  import clickEffect from "@/assets/sounds/live_click.wav";
  import { LotStatuses } from "@/enum";
  import { isScreenL } from "@/utils/media-size";
  const audioClick = new Audio(clickEffect);
  audioClick.preload = "auto";

  watch(activeLot, (newValue, oldValue) => {
    refreshBidVariants();

    if (newValue?.id !== oldValue?.id) {
      selectItem(bidVariants.value[0]);
    } else {
      selectItem(Math.max(selectedBid.value, bidVariants.value[0]));
    }
  });

  const createSelectedBidWatcher = (sourceRef: Ref<number | undefined>) => {
    watch(sourceRef, (newValue, oldValue) => {
      newValue = newValue ?? 0;
      oldValue = oldValue ?? 0;

      const isMinimalVariantSelected = selectedBid.value === bidVariants.value[0];

      refreshBidVariants();

      if (isMinimalVariantSelected && newValue < oldValue) {
        selectItem(bidVariants.value[0]);
      } else {
        selectItem(Math.max(selectedBid.value, bidVariants.value[0]));
      }
    });
  }

  createSelectedBidWatcher(currentBid);
  createSelectedBidWatcher(bidsCount);
  createSelectedBidWatcher(userBid);


  const handleVisibilityChange = () => {
    isPageVisible.value = !document.hidden;
  };

  onMounted(() => {
    refreshBidVariants();
    selectItem(bidVariants.value[0]);
    document.addEventListener("visibilitychange", handleVisibilityChange);
  });

  onUnmounted(() => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  });

  const selectedBidMinus = () => {
    const currentIndex = bidVariants.value.indexOf(selectedBid.value);
    if (currentIndex > 0) {
      selectedBid.value = bidVariants.value[currentIndex - 1];
    }
  };

  const selectedBidPlus = () => {
    const currentIndex = bidVariants.value.indexOf(selectedBid.value);
    selectedBid.value = bidVariants.value[currentIndex + 1];

    if (bidVariants.value.length - currentIndex < 25) {
      auctionStore.addBidVariants();
      refreshBidVariants();
    }
  };

  const refreshBidVariants = () => {
    bidVariants.value = auctionStore.filteredBidVariants();
  };

  const selectItem = (item: number) => {
    selectedBid.value = item;
  };

  const placeBid = (amount: number) => {
    if (activeLot.value?.id) {
      Api.post("/live/lots/" + activeLot.value.id + "/place-bid", { amount })
          .then((response) => {
            if (response.data.success && !auctionStore.user?.is_admin) {
              playSound();
            }
          })
          .catch((error) => {
            console.log(error);

            const errors = error.response.data.errors;
            Object.keys(errors).forEach((key) => {
              errors[key].forEach((error: IPopupError) => {
                toast.add({
                  severity: "error",
                  summary: error.title,
                  detail: error.text,
                  life: 6000
                });
              });
            });
          });
    }
  };

  const playSound = () => {
    audioClick.currentTime = 0;
    audioClick.play();
  };

  const openLockBiddingTab = () => {
    emit("changeTab", "lock");
  };
</script>
